/** Adaptive quiz builder: the shapes the screen works with, and the Bloom's points rule. */

/** A question the teacher has picked, in the shape the create API stores. */
/**
 * A pick's identity. A question id is only unique inside its own skill - two skills can both call
 * a template "q1" - so the skill is part of the key. Without this, picking the same-named question
 * from a second skill would silently replace the first.
 */
export const questionKey = (q: { type: string; lo_id?: string; question_id: string }): string =>
  `${q.type}::${q.lo_id || ''}::${q.question_id}`;

export interface PickedQuestion {
  /** Same values adaptive_questions uses: a skill question, or a HOTS. */
  type: 'AdaptivePractice' | 'HOTS';
  /** Engine id: a skill question template id, or the HOTS id. */
  question_id: string;
  /** The skill it came from - AdaptivePractice only (a HOTS owns no skill). */
  lo_id?: string;
  /** AdaptivePractice only: {mappedContent:{dbVars}, answerObj} - what freezes the question. */
  review_obj?: any;
  /** Coins for a correct answer. Defaults from blooms, the teacher can change it. */
  points: number;
  /** Bloom's level the engine reported. Kept on screen only - never sent, never stored. */
  blooms?: string;
  /** Skill name. Screen only. */
  skillName?: string;
  /** How the question starts, for the "Selected" list. Screen only. */
  label?: string;
  /** The engine's whole answer, so a picked question can still be viewed. Screen only. */
  response?: any;
}

/**
 * Parts a question is never read from: the answer area and the controls in it. A number board is a
 * grid of buttons, so collecting its text gives "1 2 3 4 5 6 7 8 9 10" - the answers to choose
 * from, not the question being asked. The names are the renderer's own (see KeysToComponentMap).
 */
const ANSWER_PARTS = ['checkanswer', 'button', 'input'];

/** Every bit of text in a rendered part, in order. `children` is either the text itself or more
 *  parts, which is how the renderer walks it too. */
const collectText = (node: any, out: string[]): void => {
  if (node === null || node === undefined) return;
  if (typeof node === 'string' || typeof node === 'number') {
    out.push(String(node));
    return;
  }
  if (Array.isArray(node)) {
    node.forEach((child) => collectText(child, out));
    return;
  }
  if (node.component && ANSWER_PARTS.indexOf(String(node.component).toLowerCase()) !== -1) return;
  if (node.text) out.push(String(node.text));
  if (node.children) collectText(node.children, out);
};

/** Does this read as a question, or is it just numbers and symbols? Written without a letter class
 *  so it holds for every language the app renders in. */
const hasWords = (text: string): boolean =>
  text.replace(/[\d\s.,:;!?'"()\-+*/=<>[\]{}|\\_^%$#@~`]/g, '').length > 0;

/**
 * How a question is named in the Selected list: how it starts, so a teacher recognises it once it
 * has left the list of questions.
 *
 * The engine already says what the question asks - `testJSON` carries `questionText`, `heading` and
 * `soundText` (the words a child hears it read in) as plain strings. Those are used in that order.
 * Reading the rendered parts instead was a guess, and it guessed wrong: a number board is a grid of
 * buttons, so a counting question came out named "1 2 3 4 …" - its answers, not its question. The
 * walk below is kept only for a question whose engine gives none of the three.
 */
export const questionLabel = (response: any, fallback = 'Question'): string => {
  const testJSON = response?.testJSON || {};
  const fromEngine = [testJSON.questionText, testJSON.heading, testJSON.soundText]
    .map((value: any) => String(value ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim())
    .find((value: string) => value.length > 0);
  // The WHOLE question, not a shortened one: how much of it fits is the panel's business, and it
  // decides that with an ellipsis of its own when it is narrow. Cutting the text here made the
  // ellipsis part of the text itself, so widening the panel could never show the rest.
  if (fromEngine) return fromEngine;

  const parts = response?.component || {};
  const textOf = (keys: string[]) => {
    const out: string[] = [];
    keys.forEach((key) => collectText(parts[key], out));
    return out.join(' ').replace(/\s+/g, ' ').trim();
  };
  const body = textOf(
    Object.keys(parts).filter(
      (key) => key !== 'heading' && key !== 'explanation' && key !== 'hint' && key !== 'answer',
    ),
  );
  const heading = textOf(['heading']);
  // A question drawn rather than written - a number line, a set of pictures - leaves nothing but
  // its labels behind. Its heading is what a teacher would call it.
  const text = hasWords(body) ? body : heading || body;
  if (!text) return fallback;
  return text;
};

/** The unit plan, flattened to what the pickers need. */
export interface SubTopic {
  id: string;
  name: string;
  loIds: string[];
}

export interface Module {
  id: string;
  name: string;
  subTopics: SubTopic[];
}

/**
 * Points default from the question's Bloom's level - a harder question is worth more. The teacher
 * can change any of them, so this is only a starting point. Same order as BLOOMS in play-service
 * (Remember 0.2 … HOTS 1.0), mapped onto the 1-10 the quiz stores.
 */
const POINTS_BY_BLOOMS: { [level: string]: number } = {
  remember: 1,
  understand: 2,
  application: 3,
  analysis: 4,
  hots: 5,
};

/**
 * Bloom's level in three letters, for a card too narrow to spell it out. The level still has to be
 * recognisable - it is how a teacher judges what a question is worth - so these are the first
 * letters of the word, not a code anyone has to learn.
 */
const BLOOMS_SHORT: { [level: string]: string } = {
  remember: 'Rem',
  understand: 'Und',
  application: 'App',
  analysis: 'Ana',
  hots: 'HOTS',
};

export const shortBlooms = (blooms?: string): string => {
  const level = String(blooms || '').trim();
  if (!level) return '';
  return BLOOMS_SHORT[level.toLowerCase()] || level.slice(0, 3);
};

export const MIN_POINTS = 1;
export const MAX_POINTS = 10;

/** Default points for a Bloom's level. An unknown or missing level is worth the least: the quiz
 *  should never inflate a question's value because the engine did not label it. */
export const pointsForBlooms = (blooms?: string): number =>
  POINTS_BY_BLOOMS[String(blooms || '').trim().toLowerCase()] ?? MIN_POINTS;

export const clampPoints = (points: number): number =>
  Math.min(MAX_POINTS, Math.max(MIN_POINTS, Math.round(points) || MIN_POINTS));

/**
 * Plan names carry a code and a title joined by a pipe ("N1 || Counting"); teachers read them with
 * the pipe replaced. Same rule the portal's own quiz and curriculum screens use.
 */
const displayName = (value: any, fallback: string): string => {
  const text = String(value ?? '').trim();
  if (!text) return fallback;
  if (text.includes(' || ')) return text.replace(' || ', '. ');
  if (text.includes(' | | ')) return text.replace(' | | ', '. ');
  return text;
};

/**
 * The classroom's unit plan, flattened to what the pickers need: every module, and under it every
 * topic its sub-modules teach, with the objectives that topic covers.
 *
 * EVERY module and topic the plan carries is offered, exactly as the old quiz builder offered them.
 * Nothing is filtered out here - not by a topic's flags, not by whether its objectives are switched
 * on for this classroom. Doing that dropped whole modules off the front of the list. What a teacher
 * can actually pick from is settled later and by the one authority on it: the skills dropdown holds
 * only the objectives the question bank can serve.
 *
 * Shape comes from skills-service fetchUnitPlanForActive:
 * modules[].sub_modules[].topics[].learning_objectives[].lo_id
 */
export const flattenUnitPlan = (collection: any): Module[] => {
  const modules: Module[] = [];
  (collection?.modules || []).forEach((module: any) => {
    const moduleId = String(module?._id || module?.module_id || '');
    if (!moduleId) return;

    // A topic can be taught across more than one sub-module; it is one entry in the picker.
    const subTopics: SubTopic[] = [];
    const seen: { [id: string]: SubTopic } = {};

    (module.sub_modules || []).forEach((subModule: any) => {
      (subModule?.topics || []).forEach((topic: any) => {
        const topicId = String(topic?._id || topic?.topic_id || '');
        if (!topicId) return;

        const loIds: string[] = [];
        (topic.learning_objectives || []).forEach((lo: any) => {
          const id = typeof lo === 'string' ? lo : lo?.lo_id;
          if (id) loIds.push(String(id));
        });

        const already = seen[topicId];
        if (already) {
          loIds.forEach((id) => {
            if (already.loIds.indexOf(id) === -1) already.loIds.push(id);
          });
          return;
        }

        const subTopic: SubTopic = {
          id: topicId,
          name: displayName(topic.name, 'Sub-topic'),
          loIds,
        };
        seen[topicId] = subTopic;
        subTopics.push(subTopic);
      });
    });

    modules.push({
      id: moduleId,
      name: displayName(module.name, 'Module'),
      subTopics,
    });
  });
  return modules;
};
